import React from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography
} from '@mui/material';
import { AuditRow } from './auditApi';
import { RESULT_STYLES, entityLabel, pharmacyLabel, sectionPath, splitDateTime, userLabel } from './auditFormat';

export const PAGE_SIZE_OPTIONS = [25, 50, 100];

const BORDER = '1px solid #e2e8f0';
const HEAD_SX = { backgroundColor: '#f8fafc', color: '#475569', fontWeight: 700, fontSize: '0.78rem', py: 1, borderBottom: BORDER } as const;
const CELL_SX = { py: 1, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' } as const;
const PRIMARY_SX = { fontSize: '0.85rem', color: '#0f172a', lineHeight: 1.35 } as const;
const SECONDARY_SX = { fontSize: '0.76rem', color: '#64748b', lineHeight: 1.35 } as const;
const CLAMP_SX = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

const ResultBadge: React.FC<{ row: AuditRow }> = ({ row }) => {
  const style = RESULT_STYLES[row.result] || RESULT_STYLES.error;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block', px: 1, py: 0.25, borderRadius: '6px', fontSize: '0.74rem', fontWeight: 700,
        color: style.color, backgroundColor: style.background, border: `1px solid ${style.border}`, whiteSpace: 'nowrap'
      }}
    >
      {row.resultTitle}
    </Box>
  );
};

/** Вторая строка действия: для ошибок и отказов - причина, для успешных - краткая сводка изменений */
function actionNote(row: AuditRow): string {
  return row.result === 'success' ? row.summary || '' : row.message || row.summary || '';
}

interface RowProps {
  row: AuditRow;
  selected: boolean;
  clickable: boolean;
  onOpen: (row: AuditRow) => void;
}

const AuditRowView = React.memo(
  function AuditRowView({ row, selected, clickable, onOpen }: RowProps) {
    const when = splitDateTime(row.createdAt);
    const note = actionNote(row);
    const object = entityLabel(row);
    const pharmacy = pharmacyLabel(row);

    return (
      <TableRow
        hover={clickable}
        selected={selected}
        onClick={clickable ? () => onOpen(row) : undefined}
        sx={{ cursor: clickable ? 'pointer' : 'default', '&.Mui-selected': { backgroundColor: '#eff6ff' } }}
      >
        <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>
          <Typography sx={PRIMARY_SX}>{when.date}</Typography>
          <Typography sx={{ ...SECONDARY_SX, fontVariantNumeric: 'tabular-nums' }}>{when.time}</Typography>
        </TableCell>
        <TableCell sx={{ ...CELL_SX, maxWidth: 220 }}>
          <Typography sx={{ ...PRIMARY_SX, ...CLAMP_SX }}>{userLabel(row)}</Typography>
          <Typography sx={{ ...SECONDARY_SX, ...CLAMP_SX }}>{row.roleName || '—'}</Typography>
          {row.actorUsername && (
            <Tooltip title="Действие выполнено в режиме имперсонации" arrow>
              <Typography sx={{ ...SECONDARY_SX, ...CLAMP_SX, color: '#b45309' }}>Реально: {row.actorUsername}</Typography>
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{ ...CELL_SX, maxWidth: 180 }}>
          <Typography sx={PRIMARY_SX}>{sectionPath(row)}</Typography>
        </TableCell>
        <TableCell sx={{ ...CELL_SX, maxWidth: 360 }}>
          <Typography sx={{ ...PRIMARY_SX, fontWeight: 600 }}>{row.actionTitle}</Typography>
          <Typography sx={SECONDARY_SX}>{row.kindTitle}</Typography>
          {note && (
            <Tooltip title={note} arrow enterDelay={400}>
              <Typography sx={{ ...SECONDARY_SX, ...CLAMP_SX, color: row.result === 'success' ? '#64748b' : '#b45309' }}>{note}</Typography>
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{ ...CELL_SX, maxWidth: 220 }}>
          {object ? (
            <Tooltip title={object} arrow enterDelay={400}>
              <Typography sx={{ ...PRIMARY_SX, ...CLAMP_SX }}>{object}</Typography>
            </Tooltip>
          ) : (
            <Typography sx={{ ...PRIMARY_SX, color: '#cbd5e1' }}>—</Typography>
          )}
          {row.period && <Typography sx={SECONDARY_SX}>Период: {row.period}</Typography>}
        </TableCell>
        <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>
          <Typography sx={{ ...PRIMARY_SX, color: pharmacy ? '#0f172a' : '#cbd5e1' }}>{pharmacy || '—'}</Typography>
        </TableCell>
        <TableCell sx={CELL_SX}>
          <ResultBadge row={row} />
        </TableCell>
      </TableRow>
    );
  }
);

interface Props {
  rows: AuditRow[];
  total: number;
  page: number;
  pageSize: number;
  selectedId: number | null;
  /** Строки открываются в панели подробностей (право audit.details.view) */
  clickable: boolean;
  /** Показаны данные предыдущего запроса, новые еще загружаются */
  stale: boolean;
  onOpen: (row: AuditRow) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

/** Таблица журнала с постраничной навигацией на сервере */
const AuditTable: React.FC<Props> = ({
  rows, total, page, pageSize, selectedId, clickable, stale, onOpen, onPageChange, onPageSizeChange
}) => (
  <Paper elevation={0} sx={{ border: BORDER, borderRadius: '12px', overflow: 'hidden', opacity: stale ? 0.6 : 1, transition: 'opacity 0.15s' }}>
    <TableContainer sx={{ maxHeight: 'calc(100vh - 440px)', minHeight: 320 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={HEAD_SX}>Дата и время</TableCell>
            <TableCell sx={HEAD_SX}>Пользователь</TableCell>
            <TableCell sx={HEAD_SX}>Модуль</TableCell>
            <TableCell sx={HEAD_SX}>Действие</TableCell>
            <TableCell sx={HEAD_SX}>Объект</TableCell>
            <TableCell sx={HEAD_SX}>Аптека</TableCell>
            <TableCell sx={HEAD_SX}>Результат</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <AuditRowView key={row.id} row={row} selected={row.id === selectedId} clickable={clickable} onOpen={onOpen} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <TablePagination
      component="div"
      showFirstButton
      showLastButton
      rowsPerPageOptions={PAGE_SIZE_OPTIONS}
      count={total}
      rowsPerPage={pageSize}
      page={page}
      onPageChange={(_, next) => onPageChange(next)}
      onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
      labelRowsPerPage="Строк на странице:"
      labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
      sx={{ borderTop: BORDER }}
    />
  </Paper>
);

export default React.memo(AuditTable);
